import {
  AgGridEvent,
  ColDef,
  ColumnMovedEvent,
  ColumnState,
  DateFilterModel,
  DragStoppedEvent,
  ExcelCell,
  ExcelExportParams,
  ExcelRow,
  FilterChangedEvent,
  GridApi,
  GridOptions,
  Module,
  SortChangedEvent,
  TextFilterModel,
  SetFilterModel,
  ValidationModule,
  ClientSideRowModelModule
} from 'ag-grid-community';
import {
  AllEnterpriseModule,
  ClipboardModule,
  ExcelExportModule,
  MasterDetailModule,
  MenuModule,
  RangeSelectionModule,
  RowGroupingModule,
  SetFilterModule
} from "ag-grid-enterprise"
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, Renderer2 } from '@angular/core';
import { Store } from '@ngrx/store';
import { DateTime } from 'luxon';
import { BehaviorSubject, Observable, Subject, Subscription, takeUntil } from 'rxjs';
import { MasterTransactionsTableActions, StandingAuthorizationsTableActions, TransactionsTableActions } from '../../../create-request/actions';
import { selectMasterTransactionsTableCurrentTab, selectTransactionsTableCurrentTab, selectBookmarkedFilterStatus } from '../../../create-request/reducers';
import { InitReleaseActions } from '../../../initiate-and-release/actions';
import { CustomErrorInitReleaseTableComponent } from '../../../initiate-and-release/components/custom-error-init-release-table.component/custom-error-init-release-table.component';
import { savePreferences } from '../../../user-preference/actions/user-preference.actions';
import { Column } from '../../../user-preference/models/Column';
import { PreferenceTab } from '../../../user-preference/models/PreferenceTab';
import { selectStandingAuthTableCurrentTab } from '../../reducers';
import { FeatureFlagService } from '../../services/feature-flag-service';
import { agFilterNames } from '../../types/AgFilterNames';
import { NoRowsFunctionInfo } from '../../types/NoRowsFunctionInfo';
import { rowSelection, TableColumn } from '../../types/TableColumn';
import { TableComponentState } from '../../types/TableComponentState';
import { NoRowsMessageTemplates, SortOptions, standingAuthTableTabName, tableName, transactionstableTabName, transactionTableTabs, logoSvg, masterTransactionsTableTabName } from '../../utilities/Constants';
import { CustomErrorStandingAuthTableComponent } from '../custom-error-standing-auth-table/custom-error-standing-auth-table.component';
import { CustomErrorTransactionTableComponent } from '../custom-error-transaction-table/custom-error-transaction-table.component';
import { SetFloatingFilterComponent } from '../set-floating-filter/set-floating-filter.component';
import { SpinnerComponent } from '../spinner/spinner.component';
import { TextFloatingFilterComponent } from '../text-floating-filter/text-floating-filter.component';
import { styles } from './table-printout';
import { PreferenceValidationService } from '../../services/preference-validation.service';
import { CustomErrorMasterTransactionsTableComponent } from '../custom-error-master-transactions-table/custom-error-master-transactions-table.component';
import { HyphenatedPipe } from '../../pipes/HyphenatedPipe';
import { MasterStatusViewActions } from '../../../create-request/actions';
import { CustomErrorMasterStandingAuthTableComponent } from '../custom-error-master-standing-auth-table/custom-error-master-standing-auth-table.component';
import { StandingAuthMasterStatusViewActions } from '../../../standing-authorizations/actions';
import { StandingAuthMasterStatusTableActions } from '../../../standing-authorizations/actions';
import { CustomErrorCreateRuleTableComponent } from '../../../initiate-and-release/components/custom-error-create-rule-table.component/custom-error-create-rule-table.component';

@Component({
  standalone: false,
  selector: 'mm-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
})
export class TableComponent implements OnInit, OnChanges, OnDestroy {
  @Input() columnDefs: ColDef[] | null;
  @Input() showPaginationMessage: boolean = true;
  @Input() defaultColumnDefs: ColDef[];
  @Input() rowData: TableColumn[] | null;
  @Input() presetFilters: {
    [key: string]: TextFilterModel | DateFilterModel | SetFilterModel;
  };
  @Input() noExportCol: string[];
  @Input() fileName: string;
  @Input() showFilterCount: boolean;
  @Input() showTableOptions: boolean;
  @Input() showFloatingFilter: boolean;
  @Input() headerHeight: number;
  @Input() rowSelection: rowSelection;
  @Input() componentState$: Observable<TableComponentState>;
  @Input() tableName: tableName;
  @Input() showDisclaimerInfoIcon: boolean = false;
  @Input() accountNumber: string;
  @Input() printfriendlyColumnDefs: ColDef[];
  @Input() rowHeight: number;
  componentStateSub: Subscription;
  @Output() gridReadyEvent = new EventEmitter<boolean>();
  @Output() showPrintFriendlyModal = new EventEmitter<boolean>();
  bookmarkIcon$ = new BehaviorSubject<string>('bookmark-outline');
  gridApi: GridApi | undefined | null;
  gridColumnApi: GridApi | undefined | null;
  displayedRows: number = 0;
  numberOfFilters: number = 0;
  toIndex: number;
  private unlistener: () => void;
  sortingOrder: [];
  private printToggle: number = -1;
  frameworkComponents: any;
  customErrorComponent: string;
  customErrorComponentParams: { noRowsMessageFn: () => NoRowsFunctionInfo };
  loadingOverlayComponent: string;
  loadingOverlayComponentParams: { loadingFn: (item: boolean) => boolean };
  columnStateBeforePrint: ColumnState[] | undefined;
  gridOptions: GridOptions;
  defaultColumnDef: ColDef = {
    filter: agFilterNames.text,
    sortingOrder: ['asc', 'desc'],
    suppressHeaderMenuButton: true,
    sortable: true,
    resizable: true,
    unSortIcon: true,
    flex: 1,
    minWidth: 120,
    floatingFilter: true,
    suppressFloatingFilterButton: true,
    suppressHeaderContextMenu: true,
    suppressHeaderFilterButton: true,
  };
  icons = {
    sortAscending: '<i class="sch-caret-up "/>',
    sortDescending: '<i class="sch-caret-down"/>',
    filter: '<i class="sch-search" />',
    sortUnSort: '<i class="sch-caret-down"/>',
  };
  modules: Module[] = [
    RowGroupingModule,
    MasterDetailModule,
    SetFilterModule,
    ExcelExportModule,
    ClipboardModule,
    RangeSelectionModule,
    MenuModule,
    ValidationModule,
    AllEnterpriseModule,
    ClientSideRowModelModule,
  ];
  tableToPrint: string;
  standingAuthTableCurrentTab: standingAuthTableTabName | '';
  transactionsTableCurrentTab: transactionstableTabName | '';
  isNormalDomLayout: boolean = true;
  currentTab: string;
  logoSvg = logoSvg;
  masterTransactionsTableCurrentTab: masterTransactionsTableTabName | '';
  masterTableName: string = '';
  isBookmarkedFilterOn: boolean = false;

  constructor(
    private store: Store,
    private featureFlagService: FeatureFlagService,
    private preferenceValidationService: PreferenceValidationService,
    private renderer2: Renderer2
  ) {}
  destroy$: Subject<boolean> = new Subject<boolean>();
  ngOnInit(): void {
    this.gridOptions = {
      rowData: this.rowData,
      columnDefs: this.columnDefs,
      defaultColDef: this.defaultColumnDef,
      rowHeight: this.rowHeight,
      theme: 'legacy',
    };
    this.frameworkComponents = {
      customTextFloatingFilter: TextFloatingFilterComponent,
      customSetFloatingFilter: SetFloatingFilterComponent,
      customErrorComponent:
        this.tableName === 'transactions' || this.tableName === 'recurring'
          ? CustomErrorTransactionTableComponent
          : this.tableName === 'standing authorizations'
          ? CustomErrorStandingAuthTableComponent
          : this.tableName === 'master transactions'
          ? CustomErrorMasterTransactionsTableComponent
          : this.tableName === 'master standing authorizations'
          ? CustomErrorMasterStandingAuthTableComponent
          : this.tableName === 'create rule'
          ? CustomErrorCreateRuleTableComponent
          : CustomErrorInitReleaseTableComponent,

      spinner: SpinnerComponent,
    };

    this.store
      .select(selectTransactionsTableCurrentTab)
      .pipe(takeUntil(this.destroy$))
      .subscribe((currentTab) => {
        this.currentTab = currentTab;
        this.gridApi?.refreshHeader();
      });

    this.customErrorComponentParams = {
      noRowsMessageFn: () => this.getNoRowsFunctionMessage(),
    };

    this.loadingOverlayComponentParams = {
      loadingFn: () => {
        return false;
      },
    };

    this.customErrorComponent = 'customErrorComponent';
    this.loadingOverlayComponent = 'spinner';

    this.displayedRows = this.setDisplayedRows();
    this.numberOfFilters = this.setNumberOfFilters();

    this.componentStateSub = this.componentState$?.subscribe((state) => {
      this.updateValues();
      this.updateSpinner(state);
    });

    this.store
      .select(selectTransactionsTableCurrentTab)
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.transactionsTableCurrentTab = value;
      });

    this.store
      .select(selectStandingAuthTableCurrentTab)
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.standingAuthTableCurrentTab = value;
      });

    this.store
      .select(selectMasterTransactionsTableCurrentTab)
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.masterTransactionsTableCurrentTab = value;
      });

    this.store
      .select(selectBookmarkedFilterStatus)
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.isBookmarkedFilterOn = value;
        if (this.isBookmarkedFilterOn) {
          this.bookmarkIcon$.next('bookmark');
        } else {
          this.bookmarkIcon$.next('bookmark-outline');
        }
      });

    this.setActiveTableEventListener();
  }

  ngOnChanges(): void {
    if (this.gridApi) {
      setTimeout(() => {
        this.updateValues();
        this.customErrorComponentParams = {
          noRowsMessageFn: () => this.getNoRowsFunctionMessage(),
        };
      });
    }
  }

  ngOnDestroy(): void {
    if (this.componentStateSub) this.componentStateSub.unsubscribe();
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  clearFilters() {
    this.gridApi?.setFilterModel(null);
  }

  onGridReady(params: any) {
    this.gridApi = params?.api;
    this.gridColumnApi = params.api;
    this.gridReady(true);
    this.updateValues();
  }

  gridReady(value: boolean) {
    this.gridReadyEvent.emit(value);
  }

  onFilterChanged(params: FilterChangedEvent) {
    this.updateValues();
  }

  onColumnMoved(params: ColumnMovedEvent) {
    this.toIndex = params?.toIndex ?? 0;
  }

  onSortChanged(params: SortChangedEvent) {
    this.handleSavePreference();
    this.gridApi?.refreshHeader();
  }

  onDragStopped(params: DragStoppedEvent) {
    this.handleSavePreference();

    const lastIndex = (params.api.getColumns()?.length ?? 0) - 1;

    if (lastIndex && this.toIndex >= lastIndex) {
      params.api.moveColumnByIndex(this.toIndex, lastIndex - 1);
    }
  }

  getGridColumns(event: AgGridEvent): Column[] {
    let columns = event.api.getAllGridColumns().map((column) => {
      let mappedColumn: Column = {
        colId: '',
        sort: null,
      };
      mappedColumn.colId = column.getColId();
      mappedColumn.sort = column.getSort() as SortOptions;
      return mappedColumn;
    });
    return columns;
  }

  private setDisplayedRows(): number {
    var displayedRows = 0;
    this.gridApi?.forEachNodeAfterFilter(() => displayedRows++);
    return displayedRows;
  }

  private setNumberOfFilters(): number {
    if (this.gridApi) {
      const filterModel = this.gridApi.getFilterModel();
      return Object.keys(filterModel).length;
    }
    return 0;
  }

  setDynamicDomLayout(): void {
    if (this.gridApi) {
      const rowCount: number = this.getCurrentPageRowCount();
      const maxHeight: number = 500;
      const size = this.gridApi.getSizesForCurrentTheme();
      const gridHeight: number = rowCount * size?.rowHeight + this.headerHeight;

      if (gridHeight >= maxHeight) {
        this.gridApi.setGridOption('domLayout', 'normal');
        this.isNormalDomLayout = true;
      } else {
        this.gridApi.setGridOption('domLayout', 'autoHeight');
        this.gridApi.resetRowHeights();
        this.isNormalDomLayout = false;
      }
    }
  }

  private setActiveTableEventListener(): void {
    this.unlistener = this.renderer2.listen(document, 'sdpsSelect', (event) => {
      this.clearFilters();
    });
  }

  getCurrentPageRowCount(): number {
    if (this.gridApi) {
      const currentPage = this.gridApi.paginationGetCurrentPage();
      const totalRows = this.gridApi.paginationGetRowCount();
      const pageSize = this.gridApi.paginationGetPageSize();
      const startResult = currentPage * pageSize + 1;
      const endResult = Math.min(startResult + pageSize - 1, totalRows);
      return endResult - startResult + 1;
    }
    // default to total rows in table
    return this.displayedRows;
  }

  onPaginationChanged() {
    this.setDynamicDomLayout();
  }

  printTable(e: MouseEvent): void {
    e.composedPath().forEach((element) => {
      let htmlElement = element as HTMLElement;
      if (htmlElement.id?.includes('standing-authorizations-table')) {
        this.store.dispatch(StandingAuthorizationsTableActions.printClicked());
        this.tableToPrint = 'Standing authorizations';
      } else if (htmlElement.id?.includes('master-transactions-table')) {
        this.showPrintFriendlyModal.emit(true);
        this.store.dispatch(
          MasterTransactionsTableActions.printFriendlyClicked()
        );
        this.tableToPrint = 'Master Transactions';
      } else if (htmlElement.id?.includes('transactions-table')) {
        this.store.dispatch(TransactionsTableActions.printClicked());
        this.tableToPrint = 'Account transactions';
      } else if (htmlElement.id?.includes('init-release-table')) {
        this.store.dispatch(InitReleaseActions.printClicked());
        this.tableToPrint = 'Pending Requests';
      } else if (htmlElement.id?.includes('recurring-table')) {
        this.store.dispatch(InitReleaseActions.printClicked());
        this.tableToPrint = 'Recurring Transactions';
      } else if (htmlElement.id?.includes('msv-standing-auth-table')) {
        this.tableToPrint = 'Move Money - Master Status Standing Authorizations'
      }
    });
    if (this.tableToPrint === 'Master Transactions') {
      this.masterTableName =
        this.masterTransactionsTableCurrentTab === 'Transaction Status'
          ? 'Status'
          : 'Recurring';
    } else {
      this.togglePrintMode();
      setTimeout(() => {
        this.togglePrintMode();
      }, 0);
    }
  }

  private getNoRowsFunctionMessage(): NoRowsFunctionInfo {
    return {
      tableName: this.tableName,
      tableTabName: this.getTabName(),
      isBookmarkFilterApplied: this.isBookmarkedFilterOn,
    };
  }

  private getTabName(): NoRowsFunctionInfo['tableTabName'] {
    switch (this.tableName) {
      case 'transactions':
        return this.transactionsTableCurrentTab;
      case 'master transactions':
        return this.masterTransactionsTableCurrentTab;
      case 'master standing authorizations':
        return 'All';
      default:
        return this.standingAuthTableCurrentTab;
    }
  }

  private togglePrintMode(): void {
    if (this.printToggle > 0) {
      this.setDynamicDomLayout();
    } else {
      this.gridApi?.setGridOption('domLayout', 'autoHeight'); //hides scroll bars and shows all cells on printout

      // Required to display custom components rendered using cellrenderer when dom is in print layout (eg: action links)
      this.gridApi?.refreshCells({ force: true });

      this.rebuildTablesForPrinting();
    }
    this.printToggle *= -1;
  }

  exportTable(): void {
    this.gridApi?.exportDataAsExcel(this.excelExportParameters);
  }

  onBookmarkFilterClicked(): void {
    this.isBookmarkedFilterOn = !this.isBookmarkedFilterOn;
    if (this.isBookmarkedFilterOn) {
      this.bookmarkIcon$.next('bookmark');
      if (this.tableName && this.tableName === 'master transactions') {
        this.store.dispatch(
          MasterStatusViewActions.getBookmarkedAccounts({
            isBookmarkFilterOn: this.isBookmarkedFilterOn,
          })
        );
      } else if (this.tableName && this.tableName === 'master standing authorizations') {
        this.store.dispatch(
          StandingAuthMasterStatusViewActions.getBookmarkedAccounts({
            isBookmarkFilterOn: this.isBookmarkedFilterOn,
          })
        );
      }
    } else {
      this.bookmarkIcon$.next('bookmark-outline');
      if (this.tableName && this.tableName === 'master transactions') {
        this.store.dispatch(MasterStatusViewActions.getMasterTransactionsList());
      } else if (this.tableName && this.tableName === 'master standing authorizations') {
        this.store.dispatch(StandingAuthMasterStatusViewActions.callLetters());
      }
    }
  }

  get displayMasterStatusViewBookmarks(): boolean {
    if (this.tableName.toLowerCase() === 'master standing authorizations') {
      return true;
    }
    if (
      this.tableName === 'master transactions' &&
      (this.masterTransactionsTableCurrentTab === 'Transaction Status' ||
        this.masterTransactionsTableCurrentTab === 'Recurring')
    ) {
      return true;
    }
    return false;
  }

  get excelExportParameters(): ExcelExportParams {
    let parameters: ExcelExportParams = {};
    let isMsvTable =
      this.fileName.toLocaleLowerCase().includes('master') ?? false;
    let isMsvStandingAuth = this.fileName.toLocaleLowerCase().includes('standing') ?? false;
    const exportFileDate = isMsvTable
      ? DateTime.local({ zone: 'America/New_York' }).toFormat(
          'MMddy.hhmmssa_ZZZZ'
        )
      : DateTime.now().toFormat('LL-dd-yyyy');

    parameters.author = '';
    parameters.processCellCallback = function (params) {
      const value = params.value;
      if (value === undefined) {
        return `${value}`;
      } else {
        return value[0] === '=' || value[0] === '+' || value[0] === '@'
          ? "'" + value
          : `${value}`;
      }
    };
    parameters.fileName = `${this.fileName}${exportFileDate}.XLSX`;
    parameters.sheetName = isMsvTable
      ? `MoveMoneyMasterTxnList_${this.accountNumber ?? 'Sheet1'}`
      : `${this.accountNumber ?? 'Sheet1'}`;
    parameters.columnKeys = this.gridColumnApi
      ?.getAllDisplayedColumns()
      .filter((column) => !this.noExportCol.includes(column.getColId()));
    if(isMsvStandingAuth){
       parameters.sheetName = `MM_MasterStAuth_${this.accountNumber ?? 'Sheet1'}`
    }

    if (isMsvTable) {
      parameters.prependContent = this.prependCells;
    }

    return parameters;
  }

  updateValues() {
    this.setDynamicDomLayout();
    if (this.gridApi) {
      this.displayedRows = this.setDisplayedRows();
      this.numberOfFilters = this.setNumberOfFilters();
    }
  }

  onRowDataUpdated(): void {
    if (!this.gridApi) return;
    const rowCount = this.gridApi.getDisplayedRowCount();
    if (rowCount > 0) {
      this.gridApi.setGridOption('suppressNoRowsOverlay', true);
      this.gridApi.hideOverlay();
    } else {
      this.gridApi.setGridOption('suppressNoRowsOverlay', false);
      this.gridApi.showNoRowsOverlay();
    }
  }

  updateSpinner(componentState: TableComponentState) {
    if (componentState.cancelLinkClicked) {
      this.gridApi?.showLoadingOverlay();
      this.loadingOverlayComponentParams = {
        loadingFn: () => {
          return true;
        },
      };
      return;
    }
    if (!this.gridApi) return;
    this.gridApi.hideOverlay();
    if (
      !componentState.loading && this.gridApi.getDisplayedRowCount() === 0
    ) {
      this.gridApi.setGridOption('suppressNoRowsOverlay', false);
      this.gridApi.showNoRowsOverlay();
    }
  }

  handleSavePreference() {
    let currentTab = this.getCurrentTab(this.tableName);
    let columns = this.gridColumnApi?.getColumnState();
    if (columns) {
      let userPreferenceRequest: PreferenceTab = {
        tabName: `${currentTab}`.toLowerCase(),
        tableName: this.tableName,
        columns: this.preferenceValidationService.validatePreferences(
          columns,
          this.defaultColumnDefs
        ),
      };
      this.store.dispatch(savePreferences({ userPreferenceRequest }));
    }
  }

  getCurrentTab(tableName: string) {
    switch (tableName) {
      case 'transactions':
        return this.transactionsTableCurrentTab;
      case 'standing authorizations':
        return this.standingAuthTableCurrentTab;
      case 'master transactions':
        return this.masterTransactionsTableCurrentTab;
      default:
        return 'all';
    }
  }

  rebuildTablesForPrinting() {
    const hyphenatedPipe = new HyphenatedPipe();
    let rows: any[] = [];
    
    if (this.gridApi) {
      const startRow = this.gridApi.paginationGetCurrentPage() * this.gridApi.paginationGetPageSize();
      const endRow = startRow + this.gridApi.paginationGetPageSize();
      
      let sortedRows = getAllSortedRows(this.gridApi, this.gridColumnApi?.getColumnState());
      
      sortedRows.forEach((data, index) => {
        if (index >= startRow && index < endRow) {
          rows.push({ data });
        }
      });
    }
      
    let printWindow = window.open(' ', '', 'height=900,width=900');

    if (printWindow !== null) {
      printWindow.document.write('<html><head><style>' + styles + '</style>');
      printWindow.document.write(
        `<title>Move Money - Schwab Advisor Center</title></head>`
      );
      printWindow.document.write(`<div class=svg-container>`);
      printWindow.document.write(this.logoSvg);
      printWindow.document.write(`</div>`);
      printWindow.document.write(`<div class="container">`);
      if (this.tableToPrint === 'Master Transactions') {
        printWindow.document.write(
          `<div class="print-friendly-text master-account">Master Account: <strong>${hyphenatedPipe.transform(
            this.accountNumber
          )}</strong></div>`
        );
        printWindow.document.write(
          `<div class="print-friendly-text table-name"><strong>Move Money - Master Status</strong></div>`
        );
        if (this.masterTableName === 'Status') {
          printWindow.document
          .write(`<div class="print-friendly-text master-disclaimer-text">
          <p><strong>Note:</strong> For Position Internal Transfers entered online that are still in Pending or Approved Status, the
            Amount field is the estimated value and will be updated with the final position values at the time of Internal Transfer
            completion. </p>
          <p> Inactive ACH Set-Ups that can be reactivated are displayed within the Account Status view. <br>
            <strong>ACH Transactions -</strong> Process Date displays the date the transfer is initiated. <br>
            <strong>Note:</strong> All transactions may not appear on this status page depending on the transaction
            details and view for your master.
          </p></div>`);
        }
        else {
          printWindow.document
          .write(`<div class="print-friendly-text master-disclaimer-text">
          <p>
            <strong>Note:</strong> All transactions may not appear on this status page depending on the transaction
            details and view for your master.
          </p></div>`);
        }
      }
      if (this.tableToPrint === 'Move Money - Master Status Standing Authorizations') {
        printWindow.document.write(
          `<div><span class="section-title">Master Account: <strong>${hyphenatedPipe.transform(
            this.accountNumber
          )}</strong></span></div>`
        );
        printWindow.document.write(
          `<div><span class="section-title">${this.tableToPrint}</span></div>`
        );
        printWindow.document.write(
          `<div><span class="section-title"><strong>Note:</strong> All transactions may not appear on this status page depending on the transaction details and view for your master.</span></div>`
        );
      } else {
        printWindow.document.write(
          `<div><span class="section-title">${this.tableToPrint}</span></div>`
        );
      }
      let columnLength = 0;
      if (this.columnDefs?.length) {
        columnLength = this.columnDefs?.filter(
          (typeName) => typeName?.field != 'Actions'
        )?.length;
      }

      if (this.tableToPrint === 'Standing authorizations') {
        printWindow.document.write(
          `<div class="label-container"><span class="tab-label">${this.standingAuthTableCurrentTab}</span></div>`
        );
        printWindow.document.write(
          `<div class="table-count"><span><strong>${rows?.length} result(s)</strong></span></div>`
        );
        printWindow.document.write(this.getTableHtmlData(columnLength, rows));
      }

      if (this.tableToPrint === 'Account transactions') {
        printWindow.document.write(
          `<div class="label-container"><span class="tab-label">${this.transactionsTableCurrentTab}</span></div>`
        );
        printWindow.document.write(
          `<div class="table-count"><span><strong>${rows?.length} result(s)</strong></span></div>`
        );
        printWindow.document.write(this.getTableHtmlData(columnLength, rows));
      }
      if (this.tableToPrint === 'Master Transactions') {
        printWindow.document.write(
          `<div class="table-count"><span><strong>${rows?.length} result(s)</strong></span></div>`
        );
        printWindow.document.write(this.getTableHtmlData(columnLength, rows));
      }

      if (this.tableToPrint === 'Move Money - Master Status Standing Authorizations') {
        printWindow.document.write(
          `<div class="table-count"><span><strong>${rows?.length} result(s)</strong></span></div>`
        );
        printWindow.document.write(this.getTableHtmlData(columnLength, rows));
        this.store.dispatch(StandingAuthMasterStatusTableActions.printClicked());
      }

      if (this.tableToPrint === 'Pending Requests') {
        printWindow.document.write(
          `<div class="table-count"><span><strong>${rows?.length} result(s)</strong></span></div>`
        );
        printWindow.document.write(this.getTableHtmlData(columnLength, rows));
      }

      if (this.tableToPrint === 'Recurring Transactions') {
        printWindow.document.write(
          `<div class="label-container"><span class="tab-label">${this.transactionsTableCurrentTab}</span></div>`
        );
        printWindow.document.write(
          `<div class="table-count"><span><strong>${rows?.length} result(s)</strong></span></div>`
        );
        printWindow.document.write(this.getTableHtmlData(columnLength, rows));
      }
    }

    printWindow?.document.write(`</div>`);
    printWindow?.document.write(`</html>`);
    printWindow?.window.print();
    printWindow?.window.close();
  }

  get height(): string {
    if (this.isNormalDomLayout) return '500px';
    else return '';
  }

  get width(): string {
    if (this.masterTransactionsTableCurrentTab) return '100%';
    else return '';
  }

  getTableHtmlData(columnGrid: number, rows: any[]): string {
    let columnsInOrder =
      this.gridColumnApi?.getColumnState().map((x) => x.colId) ?? [];
    let columnHtmlText: string = '';
    let gridTemplateColumns = new Array(columnGrid).fill('auto').join(' ');
    let columnHtmlTextGridStyle = `display: grid;grid-template-columns: ${gridTemplateColumns};`;
    columnsInOrder
      ?.filter((x) => x !== 'Actions')
      .forEach((col) => {
        columnHtmlText += `<div class="header-row table-cell">${col}</div>`;
      });
    columnHtmlText = `<div style="${columnHtmlTextGridStyle}">${columnHtmlText}`;

    let rowHtmlText: string = '';
    return `
      ${columnHtmlText}
      ${rows
        ?.map((row) => {
          rowHtmlText = '';
          const rowData = row && typeof row === 'object' && 'data' in row ? row.data : row;
          columnsInOrder
            ?.filter((x) => x !== 'Actions' && x)
            .forEach((col) => {
              rowHtmlText += `<div class="table-cell">${rowData?.[col ?? ''] ?? ''}</div>`;
            });
          return rowHtmlText;
        })
        .join('')}
    </div>`;
  }

  get prependCells(): ExcelRow[] {
    let exportPrependDate = DateTime.local({
      zone: 'America/New_York',
    }).toFormat('MM/dd/y hh:mm:ss a ZZZZ');
    let Header1stLineText =
      this.masterTransactionsTableCurrentTab === 'Recurring'
        ? cell(
            `Exported from MoveMoney Master Account Recurring Check / Internal Transfer / ACH Transaction records on "${exportPrependDate}"`,
            'header'
          )
        : cell(
            `Exported from MoveMoney Master Account Transaction records on "${exportPrependDate}"`,
            'header'
          );
        if(this.tableName === 'master standing authorizations')
          Header1stLineText= cell(
            `Exported from MoveMoney Master Account On-Request Standing Authorizations / ACH records on  "${exportPrependDate}"`,
            'header'
          )

    let res: ExcelRow[] = [
      {
        cells: [Header1stLineText],
      },
      {
        cells: [cell('', 'header')],
      },
      {
        cells: [cell(`Total Records exported ${this.displayedRows}`, 'header')],
      },
      {
        cells: [cell('', 'header')],
      },
    ];
    return res;
  }

  printFriendlyButtonClicked(printButtonClicked: boolean) {
    this.rebuildTablesForPrinting();
  }
}

function cell(text: string, styleId?: string): ExcelCell {
  return {
    styleId: styleId,
    data: {
      type: /^\d+$/.test(text) ? "Number" : "String",
      value: String(text),
    },
  };
}

export function getAllSortedRows(gridApi: GridApi<any> | undefined | null, columns: ColumnState[] | undefined | null) {
  let rowData: any[] = [];
  gridApi?.forEachNodeAfterFilter(node => rowData.push(node.data));
  let columnSortPreference = columns?.find(preference => preference.sort);

  //Sort rows based on column sort preference?
  if (!rowData || rowData.length <= 1 || !columnSortPreference?.colId) {
    return rowData;
  }
  return [...rowData].sort((a, b) => {
    var n1 = a[columnSortPreference?.colId];
    var n2 = b[columnSortPreference?.colId];
    if (columnSortPreference?.colId === 'Process date' || columnSortPreference?.colId === 'Next process date') {
      // agGrid sort evaluates 'N/A' < '04/25/2025' (any date formatted as such) to true
      // Typescript evaluates 'N/A' > '04/25/2025' to true
      // When date is 'N/A', set to the lowest date possible for getTime() method to flip the evaluation
      if (n1 === 'N/A'){
        n1 = '01/01/1970';
      }
      if (n2 === 'N/A'){
        n2 = '01/01/1970';
      }
      // convert to milliseconds since 01/01/1970 for numerical comparison
      n1 = new Date(n1).getTime();
      n2 = new Date(n2).getTime();
    }
    if (columnSortPreference?.colId === 'Amount' || columnSortPreference?.colId === 'Available cash'){
      // agGrid appears to be sorting currency values by their absolute values
      // format currency values to do that comparison here
      n1 = Number(n1.replace(/[^0-9.-]+/g,""));
      n2 = Number(n2.replace(/[^0-9.-]+/g,""));
      n1 = Math.abs(n1);
      n2 = Math.abs(n2);
    }
    if (n1 < n2){
      return columnSortPreference?.sort === 'desc' ? 1 : -1;
    } else if (n1 > n2) {
      return columnSortPreference?.sort === 'asc' ? 1 : -1;
    } else {
      return 0;
    }
  });
}
